/**
 * CannonAnimationOverlay - Cinematic "ACME Human Cannonball" Animation Overlay
 *
 * Displays a theatrical cartoon overlay featuring an ACME striped circus cannon rolling out,
 * the player's card climbing in rear-first with dangling kicking legs, a burning sparking fuse,
 * a dramatic "3... 2... 1... KABOOM!" blast with smoke clouds and cannon recoil,
 * the card streaking across a dotted parabolic flight path high above the board tracks,
 * and lawn-darting head-first into the destination hole with vibrating cartoon impact lines
 * and spinning golden dizzy stars.
 */

let activeCannonOverlayInstance = null;

export class CannonAnimationOverlay {
  /**
   * Displays the ACME Human Cannonball animation overlay.
   *
   * @param {Object} [options]
   * @param {string} [options.playerName] - Name of player jumping (e.g. "You", "Computer")
   * @param {string} [options.who] - Seat ID ('p1', 'p2', etc.)
   * @param {number} [options.fromHole] - Jump launch/spring hole
   * @param {number} [options.toHole] - Destination landing hole
   * @param {number} [options.diff] - Net score difference
   * @param {boolean} [options.isForward] - True if jumping forward
   * @param {Object} [options.card] - Card object { r, s } representing the player's card
   * @param {Object} [options.settings] - Game settings object
   * @param {Function} [options.onComplete] - Callback on dismiss or completion
   * @returns {CannonAnimationOverlay|null}
   */
  static show(options = {}) {
    const settings = options.settings || (typeof window !== 'undefined' ? window.settings : null);
    if (settings) {
      const cannonVal = settings.jumpAnimation !== undefined ? settings.jumpAnimation : settings.cannonAnimation;
      if (cannonVal === 'no' || cannonVal === 'off' || cannonVal === false) {
        if (typeof options.onComplete === 'function') options.onComplete();
        return null;
      }
    }

    if (typeof document === 'undefined') {
      if (typeof options.onComplete === 'function') options.onComplete();
      return null;
    }

    if (activeCannonOverlayInstance) {
      activeCannonOverlayInstance.close(false);
    }

    const instance = new CannonAnimationOverlay(options);
    activeCannonOverlayInstance = instance;
    instance.mount();
    return instance;
  }

  static hide() {
    if (activeCannonOverlayInstance) {
      activeCannonOverlayInstance.close(true);
    }
  }

  constructor(options = {}) {
    this.options = options;
    this.onComplete = options.onComplete || (() => {});
    this.overlayEl = null;
    this.autoCloseTimer = null;
    this.stepTimers = [];
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  formatCard(card, fallbackRank = 'J', fallbackSuit = '♠') {
    if (!card) {
      return { r: fallbackRank, s: fallbackSuit, isRed: fallbackSuit === '♥' || fallbackSuit === '♦' };
    }
    if (Array.isArray(card) && card.length > 0) {
      card = card[card.length - 1];
    }
    if (typeof card === 'string') {
      const match = card.match(/^([0-9AJQK]+)([HDSC♥♦♠♣]?)$/i);
      if (match) {
        card = { r: match[1], s: match[2] };
      }
    }
    let r = card.r !== undefined ? card.r : (card.rank !== undefined ? card.rank : fallbackRank);
    if (r === 1 || r === '1') r = 'A';
    else if (r === 11 || r === '11') r = 'J';
    else if (r === 12 || r === '12') r = 'Q';
    else if (r === 13 || r === '13') r = 'K';
    r = String(r).toUpperCase();

    let s = card.s || card.suit || fallbackSuit;
    if (s === 'H' || s === 'h') s = '♥';
    else if (s === 'D' || s === 'd') s = '♦';
    else if (s === 'S' || s === 's') s = '♠';
    else if (s === 'C' || s === 'c') s = '♣';

    const isRed = s === '♥' || s === '♦';
    return { r, s, isRed };
  }

  buildHtml() {
    const pName = this.options.playerName || 'Player';
    const fromHole = this.options.fromHole !== undefined ? this.options.fromHole : 15;
    const toHole = this.options.toHole !== undefined ? this.options.toHole : 35;
    const diff = this.options.diff !== undefined ? this.options.diff : (toHole - fromHole);
    const diffStr = diff > 0 ? `+${diff}` : `${diff}`;
    const card = this.formatCard(this.options.card, 'K', '♠');

    return `
      <div class="sky-ambient-glow" aria-hidden="true"></div>

      <!-- Skip Button -->
      <button type="button" class="cannon-skip-btn" id="cannonSkipBtn" aria-label="Skip Jump Animation">
        Skip ✕
      </button>

      <!-- Banner Header -->
      <div class="cannon-banner">
        <div class="cannon-badge">
          <span>🚀 ACME HUMAN CANNONBALL!</span>
        </div>
        <h2 class="cannon-title">${pName} Launched Into The Stratosphere!</h2>
        <div class="cannon-subtitle">
          Jump Spring Hole ${fromHole} ➔ Destination Hole ${toHole} (${diffStr} Holes!)
        </div>
      </div>

      <!-- The Main Cannon Arena Stage -->
      <div class="cannon-stage" id="cannonStage">
        <div class="stage-sky-stars" aria-hidden="true"></div>

        <!-- Parabolic Trajectory Flight Arc SVG -->
        <svg class="parabolic-flight-svg" id="trajectorySvg" viewBox="0 0 860 440" preserveAspectRatio="none">
          <path class="trajectory-line" d="M 180 320 Q 480 80 770 380" />
        </svg>

        <!-- Destination Target Hole Crater (Right Side) -->
        <div class="destination-crater" id="destCrater">
          <div class="crater-rim"></div>
          <div class="hole-badge">Hole ${toHole}</div>
        </div>

        <!-- Arena Ground Track with Peg Hole Markers -->
        <div class="stage-ground">
          <div class="ground-track-holes">
            <span class="track-dot"></span>
            <span class="track-dot"></span>
            <span class="track-dot"></span>
            <span class="track-dot"></span>
            <span class="track-dot"></span>
            <span class="track-dot"></span>
            <span class="track-dot"></span>
            <span class="track-dot"></span>
            <span class="track-dot"></span>
            <span class="track-dot"></span>
          </div>
        </div>

        <!-- Digital / Comic Countdown: 3... 2... 1... KABOOM! -->
        <div class="cannon-countdown" id="cannonCountdown">3</div>

        <!-- Explosive Muzzle Blast Flash -->
        <div class="muzzle-blast-flash" id="muzzleFlash"></div>

        <!-- The ACME Striped Cannon Container -->
        <div class="acme-cannon-wrap" id="acmeCannon">
          <!-- Rear Spoked Wheel -->
          <div class="cannon-wheel back">
            <div class="wheel-hub"></div>
          </div>

          <!-- Heavy Iron Carriage -->
          <div class="cannon-carriage">
            <span class="acme-stencil">ACME</span>
          </div>

          <!-- Red & Yellow Striped Barrel -->
          <div class="cannon-barrel">
            <div class="muzzle-ring"></div>
            <div class="muzzle-cavity"></div>

            <!-- Gunpowder Fuse on Breech -->
            <div class="cannon-fuse burning" id="cannonFuse">
              <div class="fuse-spark"></div>
            </div>

            <!-- Card Stuffed Inside Cannon (Feet Kicking Out Muzzle) -->
            <div class="card-in-cannon visible kicking" id="cardInCannon">
              <div class="cannon-legs">
                <div class="c-leg l"><div class="c-foot"></div></div>
                <div class="c-leg r"><div class="c-foot"></div></div>
              </div>
            </div>
          </div>

          <!-- Front Spoked Wheel -->
          <div class="cannon-wheel front">
            <div class="wheel-hub"></div>
          </div>
        </div>

        <!-- The Flying Card in Stratospheric Flight -->
        <div class="flying-card ${card.isRed ? 'red' : 'black'}" id="flyingCard">
          <div class="aviator-cap">
            <div class="goggle-lens"></div>
            <div class="goggle-lens"></div>
          </div>
          <div class="corner tl">
            <span class="r">${card.r}</span>
            <span class="s">${card.s}</span>
          </div>
          <div class="card-rank-center">
            <span class="p-pip">${card.s}</span>
          </div>
          <div class="corner br">
            <span class="r">${card.r}</span>
            <span class="s">${card.s}</span>
          </div>
        </div>

        <!-- Lawn-Dart Card Stuck Head-First in Destination Crater -->
        <div class="lawn-dart-card ${card.isRed ? 'red' : 'black'}" id="lawnDartCard">
          <div class="lawn-dart-legs">
            <div class="d-leg"><div class="d-foot"></div></div>
            <div class="d-leg"><div class="d-foot"></div></div>
          </div>
          <div class="card-rank-center">
            <span class="p-pip">${card.s}</span>
          </div>
          <div class="corner br">
            <span class="r">${card.r}</span>
            <span class="s">${card.s}</span>
          </div>
        </div>

        <!-- Impact Shockwave Rings -->
        <div class="impact-shockwave" id="impactShockwave"></div>

        <!-- Dizzy Golden Cartoon Stars Halo -->
        <div class="dizzy-stars-halo" id="dizzyStarsHalo">
          <span class="cartoon-star">⭐</span>
          <span class="cartoon-star">✨</span>
          <span class="cartoon-star">⭐</span>
          <span class="cartoon-star">✨</span>
        </div>
      </div>
    `;
  }

  mount() {
    let el = document.getElementById('cannonAnimationOverlay');
    if (!el) {
      el = document.createElement('div');
      el.id = 'cannonAnimationOverlay';
      el.className = 'cannon-animation-overlay';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-label', 'ACME Human Cannonball Jump Animation');
      document.body.appendChild(el);
    }

    this.overlayEl = el;
    el.innerHTML = this.buildHtml();

    const skipBtn = el.querySelector('#cannonSkipBtn');
    if (skipBtn) {
      skipBtn.addEventListener('click', () => this.close(true));
    }

    window.addEventListener('keydown', this.handleKeyDown);

    // Force layout flush before activating CSS transitions
    void el.offsetWidth;
    el.classList.add('active');

    this.startChoreography();
  }

  getAudio() {
    return (typeof window !== 'undefined' && (window.audio || window.audioManager || window.sfx || (window.CC && (window.CC.audio || window.CC.audioManager || window.CC.sfx)))) || null;
  }

  startChoreography() {
    const audio = this.getAudio();

    // 0.0s: Cannon rolls out into position with squeaking iron wheels, fuse sparks
    if (audio && typeof audio.cannonRollWheel === 'function') {
      audio.cannonRollWheel();
    }
    if (audio && typeof audio.cannonFuseHiss === 'function') {
      audio.cannonFuseHiss();
    }

    // 0.4s: Countdown "3..."
    this.scheduleStep(450, () => {
      const cd = this.overlayEl?.querySelector('#cannonCountdown');
      if (cd) {
        cd.textContent = '3...';
        cd.classList.add('active');
      }
    });

    // 0.8s: Countdown "2..."
    this.scheduleStep(850, () => {
      const cd = this.overlayEl?.querySelector('#cannonCountdown');
      if (cd) {
        cd.textContent = '2...';
        cd.classList.remove('active');
        void cd.offsetWidth;
        cd.classList.add('active');
      }
    });

    // 1.2s: Countdown "1..."
    this.scheduleStep(1250, () => {
      const cd = this.overlayEl?.querySelector('#cannonCountdown');
      if (cd) {
        cd.textContent = '1...';
        cd.classList.remove('active');
        void cd.offsetWidth;
        cd.classList.add('active');
      }
    });

    // 1.6s: KABOOM! Explosive blast, cannon recoil, muzzle flash & smoke clouds
    this.scheduleStep(1600, () => {
      const cannon = this.overlayEl?.querySelector('#acmeCannon');
      const cd = this.overlayEl?.querySelector('#cannonCountdown');
      const flash = this.overlayEl?.querySelector('#muzzleFlash');
      const cardInCannon = this.overlayEl?.querySelector('#cardInCannon');
      const flyingCard = this.overlayEl?.querySelector('#flyingCard');
      const trajectory = this.overlayEl?.querySelector('#trajectorySvg');

      if (cd) {
        cd.textContent = '💥 KABOOM!';
        cd.style.color = '#ef4444';
      }
      if (cannon) cannon.classList.add('recoil');
      if (flash) flash.classList.add('boom');
      if (cardInCannon) cardInCannon.classList.remove('visible');

      this.spawnBlastSmoke();

      if (audio && typeof audio.cannonKaboom === 'function') {
        audio.cannonKaboom();
      }

      // Launch flying card into parabolic flight path
      setTimeout(() => {
        if (flyingCard) flyingCard.classList.add('in-flight');
        if (trajectory) trajectory.classList.add('active');
        if (audio && typeof audio.cannonFlightWhistle === 'function') {
          audio.cannonFlightWhistle();
        }
      }, 50);
    });

    // 2.85s: Lawn-dart head-first landing impact into destination crater
    this.scheduleStep(2850, () => {
      const flyingCard = this.overlayEl?.querySelector('#flyingCard');
      const lawnDart = this.overlayEl?.querySelector('#lawnDartCard');
      const shockwave = this.overlayEl?.querySelector('#impactShockwave');
      const trajectory = this.overlayEl?.querySelector('#trajectorySvg');

      if (flyingCard) flyingCard.style.opacity = '0';
      if (trajectory) trajectory.classList.remove('active');
      if (lawnDart) lawnDart.classList.add('impact');
      if (shockwave) shockwave.classList.add('active');

      this.spawnImpactDust();

      if (audio && typeof audio.lawnDartThud === 'function') {
        audio.lawnDartThud();
      }
    });

    // 3.1s: Dizzy stars halo spins above stuck card
    this.scheduleStep(3150, () => {
      const starsHalo = this.overlayEl?.querySelector('#dizzyStarsHalo');
      if (starsHalo) starsHalo.classList.add('active');

      if (audio && typeof audio.dizzyStarsChime === 'function') {
        audio.dizzyStarsChime();
      }
    });

    // 4.65s: Auto-close overlay gracefully
    this.autoCloseTimer = setTimeout(() => {
      this.close(true);
    }, 4650);
  }

  scheduleStep(delay, fn) {
    const t = setTimeout(fn, delay);
    this.stepTimers.push(t);
  }

  spawnBlastSmoke() {
    const stage = this.overlayEl?.querySelector('#cannonStage');
    if (!stage) return;

    for (let i = 0; i < 9; i++) {
      const puff = document.createElement('div');
      puff.className = 'cannon-smoke-puff';
      const sz = 24 + Math.random() * 38;
      puff.style.width = `${sz}px`;
      puff.style.height = `${sz}px`;
      puff.style.left = '160px';
      puff.style.bottom = '115px';
      const angle = (Math.PI * -0.3) + (Math.random() * Math.PI * 0.6);
      const dist = 40 + Math.random() * 80;
      const sx = Math.cos(angle) * dist;
      const sy = Math.sin(angle) * dist;
      puff.style.setProperty('--sx', `${sx}px`);
      puff.style.setProperty('--sy', `${sy}px`);
      stage.appendChild(puff);
      setTimeout(() => puff.remove(), 900);
    }
  }

  spawnImpactDust() {
    const stage = this.overlayEl?.querySelector('#cannonStage');
    if (!stage) return;

    for (let i = 0; i < 7; i++) {
      const puff = document.createElement('div');
      puff.className = 'cannon-smoke-puff';
      const sz = 18 + Math.random() * 26;
      puff.style.width = `${sz}px`;
      puff.style.height = `${sz}px`;
      puff.style.right = '80px';
      puff.style.bottom = '55px';
      const angle = (Math.PI * 0.1) + (Math.random() * Math.PI * 0.8);
      const dist = 25 + Math.random() * 50;
      const sx = (Math.random() - 0.5) * 60;
      const sy = -Math.sin(angle) * dist;
      puff.style.setProperty('--sx', `${sx}px`);
      puff.style.setProperty('--sy', `${sy}px`);
      stage.appendChild(puff);
      setTimeout(() => puff.remove(), 800);
    }
  }

  handleKeyDown(e) {
    if (e.key === 'Escape' || e.key === 'Esc' || e.key === 'Enter' || e.key === ' ') {
      this.close(true);
    }
  }

  close(triggerCallback = true) {
    window.removeEventListener('keydown', this.handleKeyDown);

    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
    this.stepTimers.forEach(t => clearTimeout(t));
    this.stepTimers = [];

    if (this.overlayEl) {
      this.overlayEl.classList.remove('active');
      setTimeout(() => {
        if (this.overlayEl && this.overlayEl.parentNode) {
          this.overlayEl.parentNode.removeChild(this.overlayEl);
        }
        this.overlayEl = null;
      }, 320);
    }

    if (activeCannonOverlayInstance === this) {
      activeCannonOverlayInstance = null;
    }

    if (triggerCallback && typeof this.onComplete === 'function') {
      const cb = this.onComplete;
      this.onComplete = null;
      try {
        cb();
      } catch (err) {
        console.error('[CannonAnimationOverlay] onComplete error:', err);
      }
    }
  }
}
